import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Bot, Play, Pause, Trash2, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentRegistry = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    type: 'foundation',
    provider: 'openai',
    capabilities: [],
    config: { provider: 'openai', model: 'gpt-4o-mini' },
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async () => {
    try {
      await axios.post(`${API}/agents`, newAgent);
      toast.success('Agent created successfully');
      setIsDialogOpen(false);
      fetchAgents();
      setNewAgent({
        name: '',
        type: 'foundation',
        provider: 'openai',
        capabilities: [],
        config: { provider: 'openai', model: 'gpt-4o-mini' },
      });
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error('Failed to create agent');
    }
  };

  const deleteAgent = async (id) => {
    try {
      await axios.delete(`${API}/agents/${id}`);
      toast.success('Agent deleted successfully');
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Failed to delete agent');
    }
  };

  const filteredAgents = agents.filter((agent) =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="animate-fade-in" data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Agent Registry
          </h1>
          <p className="text-sm sm:text-base text-gray-400">Manage and monitor all AI agents across your organization</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary w-full sm:w-auto" data-testid="create-agent-btn">
              <Plus className="w-5 h-5 mr-2" />
              Create Agent
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border border-gray-700 text-white">
            <DialogHeader>
              <DialogTitle>Create New Agent</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Agent Name</label>
                <Input
                  placeholder="Enter agent name"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="agent-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Agent Type</label>
                <Select
                  value={newAgent.type}
                  onValueChange={(value) => setNewAgent({ ...newAgent, type: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="agent-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="foundation">Foundation</SelectItem>
                    <SelectItem value="workflow">Workflow</SelectItem>
                    <SelectItem value="autonomous">Autonomous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Provider</label>
                <Select
                  value={newAgent.provider}
                  onValueChange={(value) => setNewAgent({ ...newAgent, provider: value, config: { ...newAgent.config, provider: value } })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="agent-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                    <SelectItem value="gemini">Google Gemini</SelectItem>
                    <SelectItem value="salesforce">Salesforce</SelectItem>
                    <SelectItem value="sap">SAP</SelectItem>
                    <SelectItem value="oracle">Oracle</SelectItem>
                    <SelectItem value="microsoft">Microsoft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Capabilities (comma-separated)</label>
                <Input
                  placeholder="text-generation, analysis, data-processing"
                  onChange={(e) => setNewAgent({ ...newAgent, capabilities: e.target.value.split(',').map(c => c.trim()) })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="agent-capabilities-input"
                />
              </div>
              <Button onClick={createAgent} className="w-full btn-primary" data-testid="submit-agent-btn">
                Create Agent
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-white/10 text-white"
            data-testid="search-agents-input"
          />
        </div>
      </div>

      {/* Agents Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="agents-grid">
          {filteredAgents.map((agent) => (
            <div key={agent.id} className="glass-panel p-6 hover:border-violet-500/50 transition-all" data-testid={`agent-card-${agent.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{agent.name}</h3>
                    <p className="text-sm text-gray-400">{agent.provider}</p>
                  </div>
                </div>
                <span className={`status-badge status-${agent.status}`}>
                  {agent.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Type:</span>
                  <span className="text-white font-medium">{agent.type}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Health:</span>
                  <span className="text-green-400 font-medium">{agent.health_score}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Security:</span>
                  <span className="text-cyan-400 font-medium">{agent.security_score}%</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {agent.capabilities.slice(0, 3).map((cap, idx) => (
                  <span key={idx} className="px-2 py-1 text-xs rounded-lg bg-violet-500/20 text-violet-300">
                    {cap}
                  </span>
                ))}
              </div>

              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1 hover:bg-white/10" data-testid={`config-agent-${agent.id}`}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-red-500/20 text-red-400"
                  onClick={() => deleteAgent(agent.id)}
                  data-testid={`delete-agent-${agent.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredAgents.length === 0 && (
        <div className="glass-panel p-12 text-center">
          <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No agents found</h3>
          <p className="text-gray-400 mb-4">Create your first agent to get started</p>
        </div>
      )}
    </div>
  );
};

export default AgentRegistry;